import React from 'react';
import { Loader2, X } from 'lucide-react';
import type { RollenSpalte } from './rollenRechteLogic';

/**
 * Zelle „Rollen" der Benutzerverwaltung (docs/rollensystem-plan.md §6.1):
 * Chips der Rollen einer Person mit × zum Entfernen und ein „+ Rolle" mit den
 * noch nicht vergebenen Rollen ihrer Firma.
 *
 * Reine Anzeige — was die Zelle zeigt, entscheidet `rollenSpalte`, geladen und
 * geschrieben (optimistisch, Sperre gegen den Doppelklick) wird in
 * `AdminUserManagement`.
 */

interface Props {
    /** Name der Person, für Hinweise und Bildschirmleser. */
    name: string;
    spalte: RollenSpalte;
    /** Rollenschlüssel dieser Person, die gerade geschrieben werden. */
    laeuft: ReadonlySet<string>;
    onSetzen: (rolle: string, an: boolean) => void;
}

const strich = (titel: string) => (
    <span className="text-xs text-slate-400" title={titel}>—</span>
);

export const BenutzerRollenChips: React.FC<Props> = ({ name, spalte, laeuft, onSetzen }) => {
    if (spalte.art === 'alle') {
        return <span className="text-xs text-slate-400" title="Admins haben immer alle Rechte und brauchen keine Rollen.">alle Rechte</span>;
    }
    if (spalte.art === 'freelancer') return strich('Freelancer bekommen keine Rollen');
    if (spalte.art === 'ohne_firma') return strich('Profil ohne Firma (company_id) — Rollen erst nach der Firmen-Zuordnung.');
    if (spalte.art === 'laedt') return <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-300" aria-label="Rollen werden geladen" />;
    if (spalte.art === 'aus') return strich(spalte.grund);
    if (spalte.chips.length === 0 && spalte.optionen.length === 0) return strich('Für diese Firma gibt es keine Rollen.');
    return (
        <div className="flex flex-wrap items-center gap-1 min-w-[10rem]">
            {spalte.chips.map(c => {
                const schreibt = laeuft.has(c.schluessel);
                return (
                    <span
                        key={c.schluessel}
                        title={c.beschreibung || undefined}
                        className="inline-flex items-center gap-0.5 rounded-full border border-indigo-100 bg-indigo-50 py-0.5 pl-2 pr-1 text-[11px] font-bold text-indigo-700"
                    >
                        {c.name}
                        <button
                            type="button"
                            onClick={() => onSetzen(c.schluessel, false)}
                            disabled={schreibt}
                            aria-label={`Rolle „${c.name}" von ${name} entfernen`}
                            title={`Rolle „${c.name}" entfernen`}
                            className="rounded-full p-0.5 text-indigo-400 hover:bg-indigo-100 hover:text-red-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {schreibt ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                        </button>
                    </span>
                );
            })}
            {spalte.optionen.length > 0 && (
                <select
                    value=""
                    onChange={e => { if (e.target.value) onSetzen(e.target.value, true); }}
                    aria-label={`Rolle für ${name} hinzufügen`}
                    title="Rolle hinzufügen — die Rechte addieren sich"
                    className="rounded-full border border-dashed border-slate-300 bg-white px-2 py-0.5 text-[11px] font-bold text-slate-500 hover:border-indigo-300 hover:text-indigo-600 cursor-pointer outline-none focus:ring-2 focus:ring-indigo-500"
                >
                    <option value="">+ Rolle</option>
                    {spalte.optionen.map(o => <option key={o.schluessel} value={o.schluessel}>{o.name}</option>)}
                </select>
            )}
        </div>
    );
};
